// FieldSelectorModal.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './FieldSelectorModal.css';

// Components
import {
	getFieldBlockOptions,
	normalizeFieldBlockFieldKeys,
} from './layoutFieldDisplay';
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';

/**
 * normalizeString
 *
 * Returns a trimmed string when the input is a string and an empty string otherwise.
 *
 * @param {unknown} value - Value to normalize.
 * @returns {string} The normalized string value.
 */
function normalizeString(value) {
	return typeof value === 'string' ? value.trim() : '';
}

/**
 * FieldSelectorModal
 *
 * Renders the field picker modal used to choose which fields a page block should show.
 *
 * @param {array} availableFields - Schema field definitions available for selection.
 * @param {boolean} isOpen - Whether the modal is visible.
 * @param {function} onClose - Closes the modal.
 * @param {function} onSelectedFieldKeysChange - Updates the current ordered field-key selection.
 * @param {function} onSave - Persists the selected fields.
 * @param {array} selectedFieldKeys - The currently selected field keys.
 * @param {string} title - Modal title text.
 *
 * @returns {ReactNode} The rendered modal.
 */
const FieldSelectorModal = ({
	availableFields = [],
	isOpen = false,
	lockedFieldKeys = [],
	onClose,
	onSelectedFieldKeysChange,
	onSave,
	selectedFieldKeys = [],
}) => {

	// ----- CONTEXT ----- //

	// Theme Context

	const { theme } = useTheme();

	// ----- DEVICE DETECTION ----- //

	// Mobile breakpoint state for modal sizing.

	const isMobile = useIsMobile();

	// ----- MEMOS ----- //

	// Available field options derived from the provided schema fields.

	const fieldOptions = useMemo(() => {
		return getFieldBlockOptions({
			schema: {
				fields: Array.isArray(availableFields) ? availableFields : [],
			},
		});
	}, [availableFields]);

	// Normalized field-key lookup for filtering and toggle validation.
	const fieldOptionKeySet = useMemo(() => {
		return new Set(fieldOptions.map((option) => option.key));
	}, [fieldOptions]);

	// Selected field keys filtered to the current option set.
	const normalizedSelectedFieldKeys = useMemo(() => {
		return normalizeFieldBlockFieldKeys(selectedFieldKeys)
			.filter((fieldKey) => fieldOptionKeySet.has(fieldKey));
	}, [fieldOptionKeySet, selectedFieldKeys]);

	// Selected field lookup for render and toggle checks.
	const selectedFieldKeySet = useMemo(() => {
		return new Set(normalizedSelectedFieldKeys);
	}, [normalizedSelectedFieldKeys]);

	const lockedFieldKeySet = useMemo(() => {
		return new Set(
			(Array.isArray(lockedFieldKeys) ? lockedFieldKeys : [])
				.map((fieldKey) => normalizeString(fieldKey))
				.filter(Boolean),
		);
	}, [lockedFieldKeys]);

	/**
	 * buildOrderedFieldKeys
	 *
	 * Preserves the current selection order while appending newly selected fields in schema order.
	 *
	 * @param {array} currentFieldKeys - Current ordered field keys.
	 * @param {array} nextValues - Next selected field keys.
	 * @returns {array} The ordered field keys to persist.
	 */
	function buildOrderedFieldKeys(currentFieldKeys, nextValues) {
		const nextSet = new Set(normalizeFieldBlockFieldKeys(nextValues));
		const preservedOrder = normalizeFieldBlockFieldKeys(currentFieldKeys)
			.filter((fieldKey) => nextSet.has(fieldKey));
		const preservedFieldKeySet = new Set(preservedOrder);
		const appendedFields = fieldOptions
			.map((option) => option.key)
			.filter((fieldKey) => nextSet.has(fieldKey) && !preservedFieldKeySet.has(fieldKey));

		return [...preservedOrder, ...appendedFields];
	}

	/**
	 * handleToggleField
	 *
	 * Toggles a field in the current selection and preserves a stable display order.
	 *
	 * @param {string} fieldKey - Field key to toggle.
	 */
	function handleToggleField(fieldKey) {
		const cleanedFieldKey = normalizeString(fieldKey);
		if (!cleanedFieldKey || !fieldOptionKeySet.has(cleanedFieldKey)) {
			return;
		}

		if (lockedFieldKeySet.has(cleanedFieldKey) && selectedFieldKeySet.has(cleanedFieldKey)) {
			return;
		}

		const nextValues = selectedFieldKeySet.has(cleanedFieldKey)
			? normalizedSelectedFieldKeys.filter((entry) => entry !== cleanedFieldKey)
			: [...normalizedSelectedFieldKeys, cleanedFieldKey];

		onSelectedFieldKeysChange?.(buildOrderedFieldKeys(normalizedSelectedFieldKeys, nextValues));
	}

	/**
	 * handleSave
	 *
	 * Persists the currently selected field keys when at least one field is selected.
	 */
	function handleSave() {
		if (!normalizedSelectedFieldKeys.length) return;
		onSave?.({ fieldKeys: normalizedSelectedFieldKeys });
	}

	// ----- RENDER ----- //

	return (

		<Modal
			isOpen={isOpen}
			height="auto"
			title="Add Fields to Block"
			width={isMobile ? '100%' : 520}
			onClose={onClose}
		>
			<ModalForm>

				<div className="field-selector-modal-container">

					{fieldOptions.length ? (
						<div
							className="field-selector-modal-list"
						>
							{fieldOptions.map((field) => {
								const description = normalizeString(field.fieldDef?.fieldDescription);
								const isChecked = selectedFieldKeySet.has(field.key);
								const isLocked = lockedFieldKeySet.has(field.key);

								return (
									<div
										className="field-selector-modal-row"
										style={{
											backgroundColor: isChecked ? theme.panel : 'transparent',
											cursor: isLocked ? 'not-allowed' : 'pointer',
											opacity: isLocked ? 0.72 : 1,
										}}
										key={field.key}
										aria-checked={isChecked}
										role="checkbox"
										tabIndex={isLocked ? -1 : 0}
										onClick={() => {
											if (isLocked) return;
											handleToggleField(field.key);
										}}
										onKeyDown={(event) => {
											if (isLocked) return;
											if (event.key !== 'Enter' && event.key !== ' ') {
												return;
											}

											event.preventDefault();
											handleToggleField(field.key);
										}}
									>
										<div aria-hidden="true">
											<Checkbox
												size={24}
												value={isChecked}
											/>
										</div>

										{/* META */}
										<div className="field-selector-modal-meta">

											{/* NAME */}
											<div
												className="field-selector-modal-name"
											>
												{field.label}
											</div>

												{/* DESCRIPTION */}
												{description ? (
												<div
													className="field-selector-modal-description"
												>
														{description}
												</div>
											) : null}
										</div>
									</div>
								);
							})}
						</div>
					) : (
						<div
							className="field-selector-modal-empty-state"
							style={{
								borderColor: theme.borderColor,
							}}
						>
							This type does not have any schema fields yet.
						</div>
					)}

					{/* SUMMARY */}
					<div
						className="field-selector-modal-summary"
					>
						{normalizedSelectedFieldKeys.length} selected
					</div>
				</div>

			</ModalForm>

			<ModalButtons>
				<Button
					iconKey="close"
					label="Cancel"
					onClick={onClose}
				/>
				<Button
					enabled={normalizedSelectedFieldKeys.length > 0}
					iconKey="save"
					label="Save Fields"
					onClick={handleSave}
				/>
			</ModalButtons>

		</Modal>

	);
};

// Dev Tools
FieldSelectorModal.displayName = 'FieldSelectorModal';

export default FieldSelectorModal;
